import { useEffect, useState } from 'react'
import { Link, createFileRoute, useRouter } from '@tanstack/react-router'
import type { OpsRelease, OpsReleases } from '@hedgehog/shared'
import { api } from '@/lib/api'
import { opsApi, useOpsMutation } from '@/components/ops/api'
import { ErrorState, FormError, LoadingState } from '@/components/app/states'
import { formatDateTime } from '@/components/app/format'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty'

// MP-9: framework releases. A release is a framework version (scripts/framework-release.ts), rolled
// out to every site without rebuilding it; rolling out an earlier release is the rollback.

export const Route = createFileRoute('/ops/releases')({
  loader: () => opsApi<OpsReleases>('/ops/releases'),
  head: () => ({ meta: [{ title: 'Releases · Ops' }] }),
  component: ReleasesPage,
  pendingComponent: () => <LoadingState label="Loading releases" />,
  errorComponent: ({ error }) => <ErrorState error={error} title="Could not load releases" />,
})

const ACTIVE = new Set(['queued', 'running', 'waiting', 'paused', 'waitingForPause'])

function ReleasesPage() {
  const { releases, sites, rollout } = Route.useLoaderData()
  const router = useRouter()
  const [target, setTarget] = useState<OpsRelease | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const { run, pending, error, setError } = useOpsMutation()
  const rolling = !!rollout && ACTIVE.has(rollout.status)

  // Refresh while a rollout runs, so site rows move to the new release as they finish.
  useEffect(() => {
    if (!rolling) return
    const t = setInterval(() => router.invalidate(), 10_000)
    return () => clearInterval(t)
  }, [rolling, router])

  const open = (r: OpsRelease) => {
    setError(null)
    setTarget(r)
    setIsOpen(true)
  }

  async function rollOut() {
    if (!target) return
    const ok = await run(() => api(`/ops/releases/${encodeURIComponent(target.id)}/rollout`, { method: 'POST' }).then(() => true))
    if (ok) setIsOpen(false)
  }

  const current = releases.find((r) => r.current)
  const behind = sites.filter((s) => (s.status === 'ready' || s.status === 'live') && s.release !== current?.id).length

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-base font-semibold">Releases</h1>
        <p className="text-muted-foreground">
          The site framework: the code that renders each site, one commit per release. Rolling out the newest release publishes every site again with its content. Roll out an earlier release to put each site back on its newest publish of that release.
        </p>
      </div>

      {rollout && (
        <p role="status" className="text-sm">
          Last rollout: <span className="font-mono">{rollout.releaseId}</span>, started {formatDateTime(rollout.startedAt)},{' '}
          <Badge variant="outline" className={rollout.status === 'complete' ? 'border-success/40 text-success' : rolling ? 'border-warning/40 text-warning' : 'border-destructive/40 text-destructive'}>
            {rollout.status}
          </Badge>
        </p>
      )}

      {releases.length === 0 ? (
        <Empty className="border border-border">
          <EmptyHeader>
            <EmptyTitle>No releases yet</EmptyTitle>
            <EmptyDescription>Deploy the API, then run bun scripts/framework-release.ts to record the first one.</EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <div className="rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Release</TableHead>
                <TableHead>Built</TableHead>
                <TableHead>Commit</TableHead>
                <TableHead>Sites</TableHead>
                <TableHead>
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {releases.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="py-1">
                    <span className="font-mono">{r.id}</span> {r.current && <Badge variant="outline" className="border-success/40 text-success">current</Badge>}
                    {r.notes && <div className="text-xs text-muted-foreground">{r.notes}</div>}
                  </TableCell>
                  <TableCell className="py-1 whitespace-nowrap text-muted-foreground">{formatDateTime(r.createdAt)}</TableCell>
                  <TableCell className="py-1 font-mono">{r.commitSha}</TableCell>
                  <TableCell className="py-1">{r.sites}</TableCell>
                  <TableCell className="py-1 text-right">
                    <Button size="xs" variant={r.current ? 'outline' : 'default'} onClick={() => open(r)} disabled={rolling}>
                      {r.current ? (behind ? `Update ${behind} site${behind === 1 ? '' : 's'}` : 'Roll out again') : 'Roll out'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <div className="flex flex-col gap-2">
        <h2 className="font-medium">Sites</h2>
        <div className="rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Site</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Release</TableHead>
                <TableHead>Last problem</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sites.map((s) => (
                <TableRow key={s.siteId}>
                  <TableCell className="py-1">
                    <Link to="/ops/orders/$orderId" params={{ orderId: s.orderId }} className="font-medium underline-offset-4 hover:underline">
                      {s.domain}
                    </Link>
                  </TableCell>
                  <TableCell className="py-1">{s.status ?? '—'}</TableCell>
                  <TableCell className="py-1 font-mono">
                    {s.release ?? <span className="text-muted-foreground">none</span>}
                    {current && s.release === current.id && <span className="ml-1 text-success">✓</span>}
                  </TableCell>
                  <TableCell className="max-w-md py-1 text-xs whitespace-pre-wrap text-muted-foreground">{s.error ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={isOpen} onOpenChange={(o) => !o && !pending && setIsOpen(false)}>
        <DialogContent className="sm:max-w-md">
          {target && (
            <>
              <DialogHeader>
                <DialogTitle>Roll out {target.id}</DialogTitle>
                <DialogDescription>
                  Every published site moves to this release, one at a time, staging sites first. A site that fails keeps its error here, the rollout goes on, and ops gets an email at the end. An earlier release puts back the content of that publish.
                </DialogDescription>
              </DialogHeader>
              <FormError message={error} />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsOpen(false)} disabled={pending}>
                  Cancel
                </Button>
                <Button type="button" onClick={rollOut} disabled={pending}>
                  {pending ? 'Starting…' : 'Roll out'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
